// @ts-strict-ignore
import { ControlGroup } from '@blueprintjs/core'
import classNames, { Value as ClassValue } from 'classnames'
import { differenceInDays, format, subDays } from 'date-fns'
import React, { ReactNode } from 'react'
import { KDPExclusiveBadge } from '../badge/KDPExclusiveBadge'
import { Input } from '../inputs/Input'
import { Callout } from '../ui/callout'
import { Card } from '../ui/card'
import { HTMLSelect } from '../ui/html-select'
import { Popover } from '../ui/popover'
import { Tooltip } from '../ui/tooltip'

import PromotionRuleI = Models.PromotionRuleI

/**
 * Presentation-only view for the Conditions + Bid Change section of the
 * Promotion Rule editor. Carved out of `PromotionRule.tsx` so the UI we're
 * iterating on can render in Storybook (and in unit tests) without dragging
 * in Redux, Recoil, react-router, or window globals.
 *
 * Everything the original implementation read from a context (currency
 * symbol, KDP flag, user start date for the lookback warning) is threaded
 * through props. The parent container is responsible for resolving these.
 *
 * Scope is deliberately limited: only the two cards the prototype refactor
 * targets. The Promotion Structure ad-group table is still owned by
 * `PromotionRule.tsx` because it pulls in MyHTMLTable / columns.jsx and
 * isn't part of the current design exploration.
 */

export interface PromotionRuleEditorPanelProps {
    rule: PromotionRuleI
    setRuleValue: <K extends keyof PromotionRuleI>(key: K) => (value: string) => void
    /** Currency prefix on $-fields. Pulled from `window.marketplace` in the
     *  container; can be set to `"$"` in tests / stories. */
    currencySymbol: string
    /** Whether the active profile is a KDP one. Gates the KDP-only metrics
     *  (Blended Profit, Adjusted Orders, etc.). Defaults to false. */
    isKdp?: boolean
    /** Beyond this many days back, we show a lookback warning callout.
     *  Container derives it from the current user's account start date. */
    recommendMaxLookbackDays: number
    /** Used inside the lookback-too-far warning copy. */
    startDate: Date
}

export const PromotionRuleEditorPanel: React.FC<PromotionRuleEditorPanelProps> = ({
    rule,
    setRuleValue,
    currencySymbol,
    isKdp = false,
    recommendMaxLookbackDays,
    startDate,
}) => {
    return (
        <>
            <Card className="p-4 mb-4">
                <Popover
                    interactionKind="hover"
                    content={
                        <div className="p-2">
                            The conditions determine when this rule will be run
                        </div>
                    }
                >
                    <Header text="Conditions" />
                </Popover>

                <div style={{ marginBottom: '15px' }}>When</div>

                <div className="ml-4">
                    <ConditionInput
                        label="Orders"
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="orders_operator"
                        valueKey="orders_value"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Blended Profit"
                        kdpOnly
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="blended_profit_operator"
                        valueKey="blended_profit_value"
                        prefix={currencySymbol}
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Adjusted Orders"
                        kdpOnly
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="adjusted_orders_operator"
                        valueKey="adjusted_orders_value"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Spend"
                        rule={rule}
                        setRuleValue={setRuleValue}
                        prefix={currencySymbol}
                        operatorKey="spend_operator"
                        valueKey="spend_value"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="ACOS"
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="acos_operator"
                        defaultOperator="<"
                        valueKey="acos_value"
                        suffix="%"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Blended ACOS"
                        kdpOnly
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="blended_acos_operator"
                        defaultOperator="<"
                        valueKey="blended_acos_value"
                        suffix="%"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Clicks"
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="clicks_operator"
                        valueKey="clicks_value"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Adjusted Pages Read"
                        kdpOnly
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="adjusted_pages_read_operator"
                        valueKey="adjusted_pages_read_value"
                        isKdp={isKdp}
                    />
                    <ConditionInput
                        label="Adj. Est. Royalties"
                        kdpOnly
                        rule={rule}
                        setRuleValue={setRuleValue}
                        operatorKey="adjusted_estimated_royalties_operator"
                        valueKey="adjusted_estimated_royalties_value"
                        prefix={currencySymbol}
                        isKdp={isKdp}
                    />

                    <InputWrapper label="How many days to look back">
                        <div className="flex flex-col">
                            <Input
                                style={{ width: '100px' }}
                                value={rule.lookback_days ?? ''}
                                onChange={(e) => setRuleValue('lookback_days')(e.target.value)}
                            />
                            <div className="text-xs text-gray-400 mt-1">
                                Leave empty for a lifetime lookback
                            </div>
                        </div>
                    </InputWrapper>

                    {rule.lookback_days &&
                        Number(rule.lookback_days) > recommendMaxLookbackDays && (
                            <Callout intent="warning" title="Warning" style={{ maxWidth: '500px' }}>
                                Your lookback period is before the first date Merch Jar has data
                                available for your account. Due to Amazon API limitations, Merch Jar
                                can only pull data for your account from{' '}
                                {format(startDate, 'M/d/yy')} onwards. Having your look back days
                                looking further back than this date may have unexpected results.
                                <br />
                                <br />
                                We'd recommend setting a lookback days of no more than{' '}
                                {recommendMaxLookbackDays} at this time. Later, as you have more
                                historical data in your Merch Jar account, you can come back and
                                safely increase your lookback days.
                            </Callout>
                        )}
                </div>
            </Card>

            <Card className="p-4">
                <Header text="Bid Change" />
                <div style={{ marginBottom: '15px' }} className="max-w-3xl text-sm text-gray-600">
                    When promoting a new keyword, you can change the{' '}
                    <a
                        className="underline text-blue-500"
                        href="https://merchjar.com/help-center/docs/smart-campaign-bid-resolution"
                        target="_blank"
                        rel="noreferrer"
                    >
                        resolved bid{' '}
                    </a>
                    for your newly promoted search term.
                </div>

                <div className="ml-4">
                    <InputWrapper label="Increase by Amount">
                        <Tooltip
                            hoverOpenDelay={300}
                            content="Increases the bid by a given amount. If you enter in $0.10, a bid of $0.50 will be increased to $0.60"
                        >
                            <Input
                                disabled={!!rule.bid_increase_percent || !!rule.bid_set}
                                style={{ width: '100px' }}
                                value={rule.bid_increase_amount ? rule.bid_increase_amount : ''}
                                onChange={(e) =>
                                    setRuleValue('bid_increase_amount')(e.target.value)
                                }
                                prefix={currencySymbol}
                                allowNegative
                            />
                        </Tooltip>
                    </InputWrapper>
                    <InputWrapper label="Increase by Percentage">
                        <Tooltip
                            hoverOpenDelay={300}
                            content="Increases the bid by the given percentage. If you enter in 10%, a bid of $0.50 will be increased to $0.55"
                        >
                            <Input
                                disabled={!!rule.bid_increase_amount || !!rule.bid_set}
                                style={{ width: '100px' }}
                                value={rule.bid_increase_percent ? rule.bid_increase_percent : ''}
                                onChange={(e) =>
                                    setRuleValue('bid_increase_percent')(e.target.value)
                                }
                                suffix="%"
                                allowNegative
                            />
                        </Tooltip>
                    </InputWrapper>
                    <InputWrapper label="Set to">
                        <Tooltip
                            hoverOpenDelay={300}
                            content="Sets the bid to the given amount. If you enter in $0.10, your new bid will be $0.10"
                        >
                            <Input
                                disabled={!!rule.bid_increase_amount || !!rule.bid_increase_percent}
                                style={{ width: '100px' }}
                                value={rule.bid_set ? rule.bid_set : ''}
                                onChange={(e) => setRuleValue('bid_set')(e.target.value)}
                                prefix={currencySymbol}
                            />
                        </Tooltip>
                    </InputWrapper>
                </div>
            </Card>
        </>
    )
}

// ─── Helpers (moved from PromotionRule.tsx) ────────────────────────────────

const operatorOptions = [
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater than or equal to' },
    { value: '=', label: 'equal to' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less than or equal to' },
]

interface OperatorSelectProps {
    value: string
    onChange: (v: string) => void
}

const OperatorSelect = ({ value, onChange }: OperatorSelectProps) => (
    <HTMLSelect
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={operatorOptions}
    />
)

interface HeaderProps {
    text: string
    className?: ClassValue
}

const Header = ({ text, className }: HeaderProps) => {
    const cn = classNames('text-xs uppercase text-gray-600 tracking-wider mb-2', className)
    return (
        <div className={cn}>
            <b>{text}</b>
        </div>
    )
}

interface InputWrapperProps {
    children: ReactNode
    label: ReactNode
    required?: boolean
}

const InputWrapper = ({ label, children, required = false }: InputWrapperProps) => (
    <div className="flex mb-3">
        <div className="mt-1 flex items-start" style={{ width: '300px' }}>
            <div className="flex items-baseline">
                {label}
                {!required && <span className="text-gray-400 text-xs">&nbsp;(optional)</span>}
            </div>
        </div>
        <div className="flex items-center gap-x-1">{children}</div>
    </div>
)

interface ConditionInputProps {
    rule: PromotionRuleI
    setRuleValue: <K extends keyof PromotionRuleI>(key: K) => (value: string) => void
    label: string
    kdpOnly?: boolean
    operatorKey: keyof PromotionRuleI
    defaultOperator?: string
    valueKey: keyof PromotionRuleI
    prefix?: ReactNode
    suffix?: ReactNode
    /** Passed in instead of read from useUser — keeps this component free of
     *  Redux so it can render standalone. */
    isKdp?: boolean
}

const ConditionInput = ({
    rule,
    setRuleValue,
    label,
    kdpOnly,
    suffix,
    prefix,
    operatorKey,
    valueKey,
    defaultOperator = '>',
    isKdp = false,
}: ConditionInputProps) => {
    if (!isKdp && kdpOnly) {
        return null
    }

    return (
        <InputWrapper
            label={
                <span className="whitespace-nowrap flex">
                    <span>{label}</span>
                    {kdpOnly && (
                        <div>
                            <KDPExclusiveBadge />
                        </div>
                    )}
                </span>
            }
        >
            <ControlGroup>
                <OperatorSelect
                    value={(rule[operatorKey] as string) || defaultOperator}
                    onChange={setRuleValue(operatorKey)}
                />
                <Input
                    style={{ width: '100px' }}
                    value={(rule[valueKey] as string) ?? ''}
                    onChange={(e) => setRuleValue(valueKey)(e.target.value)}
                    suffix={suffix}
                    prefix={prefix}
                />
            </ControlGroup>
        </InputWrapper>
    )
}

// ─── Helper for containers to derive `recommendMaxLookbackDays` ───────────

/** Container-side helper: given the user's account start date (or undefined
 *  for new accounts), return the lookback threshold beyond which the warning
 *  callout should fire. Mirrors the previous inline calculation in
 *  PromotionRule.tsx exactly. */
export function deriveRecommendMaxLookbackDays(userStartDate: string | null | undefined): {
    startDate: Date
    recommendMaxLookbackDays: number
} {
    const startDate: Date = userStartDate ? new Date(userStartDate) : subDays(new Date(), 60)
    const recommendMaxLookbackDays: number = startDate
        ? Math.abs(differenceInDays(new Date(), startDate))
        : 60
    return { startDate, recommendMaxLookbackDays }
}
